/* eslint-disable no-console */
import dayjs from 'dayjs';
import { convertQueryCommandToQueryString, IQueryCommandOptions, ISearchCommandOptions } from '@llws/dynamic-router-utils';
import { isEqual } from 'lodash-es';
import {
  IApiResponse,
  IBaseFieldConfig,
  IGenericObject,
  IMetaResponse,
  IModifyPayload,
  IObjectWithTranslations,
  IRemovePayload,
  mergeQueryCommandOptionsV2,
  subsetObject,
  Nullable,
  IJunctionPostMetaResponse,
} from '@/shared';
import { IFilterPayloadV2 } from '../../shared/interfaces/filter-payload-v2';
import { IExportFilterPayloadV2 } from '../../shared/interfaces/export-filter-payload-v2';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IStoreBaseImplementationConstructV2 } from '@/shared/interfaces/store-base-implementation-construct-v2';
import { IAction, IStateStrictV2 } from '@/shared/interfaces/store-base';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getTranslationsByClientIdAndModel, saveTranslationsInObjects } from '@/shared/services/translations';
import { translationFormatter } from '@/shared/helpers/translation-formatter';
import vm from '@/main';
import { bulkDeleteRequest, bulkSaveRequest } from '../helpers/bulk-save-request';
import { baseRequest } from '../request-configs/base-request';
import appConfig from '@/config';

type TApiResponseWithUnion<DataObjectType> = IApiResponse<DataObjectType | DataObjectType[], IMetaResponse>;

export class StoreBaseImplementationV2<DataObjectType extends { id?: number | null | undefined }> {
  /**
   * StoreBaseImplementation Class Constructor
   * @param {IStoreBaseImplementationConstructV2} config - to set up
   * @constructor
   * @example ```
   * const base = new StoreBaseImplementation<ISuiteTypes>({
   *   adminGatewayResourcePath,
   *   defaultGetSingleFilters,
   *   defaultGetListFilters,
   *   showLogging,
   * });
   * ```
   */
  constructor(config: IStoreBaseImplementationConstructV2) {
    if (config.showLogging && window.APP_CONFIG?.VUE_APP_ENV === 'development') {
      this.showLogging = config.showLogging;
    }

    this.resourcePath = config.adminGatewayResourcePath || '';
    this.translationSettings = config.translationSettings;

    if (this.resourcePath) {
      this.defaultGetListFilters = { ...config.defaultGetListFilters && config.defaultGetListFilters };
      this.defaultGetSingleFilters = { ...config.defaultGetSingleFilters && config.defaultGetSingleFilters };
      this.listViewName = config.listViewName || '';
    }
  }

  public namespaced: boolean = true;
  public showLogging: boolean = false;
  public resourcePath: string = '';
  public listViewName: string = '';
  public defaultGetSingleFilters: IQueryCommandOptions = {};
  public defaultGetListFilters: IQueryCommandOptions = {};
  public translationSettings?: INewTranslationSettingsStoreConfig = undefined;

  /**
   * Initial State of the Base Store
   * @example ```
   * export default {
   *   state: {
   *     ...base.state,
   *     floorplanResponse: null,
   *   } as IUnitCustomState
   * }
   * ```
   */
  public state: IStateStrictV2<DataObjectType> = {
    changes: {},
    currentListQuery: {},
    currentSingleQuery: {},
    list: undefined,
    single: undefined,
  };

  /**
   * Initial Getters of the Base Store
   * @example ```
   * export default {
   *  getters: {
   *     ...base.getters,
   *     floorplanResponse: (state: IUnitCustomState) => [ ...(state.floorplanResponse?.data || []) ],
   *   }
   * }
   * ```
   */
  public getters = {
    /**
     * List Data Getter <br>
     * @param {IStateStrictV2<DataObjectType>} state - state from which get list.data
     * @returns {DataObjectType[]} - either state.list.data or an empty array
     */
    list: (
      state: IStateStrictV2<DataObjectType>,
    ): DataObjectType[] => {
      if (this.showLogging) console.log('--> STOREBASE - getters/list');
      return state.list ? [...state.list.data as DataObjectType[]] : [];
    },

    /**
     * List Meta Getter <br>
     * @param {IStateStrictV2<DataObjectType>} state - state from which get list.meta
     * @returns {(IMetaResponse | undefined)} - either state.list.meta or undefined
     */
    listMeta: (
      state: IStateStrictV2<DataObjectType>,
    ): IMetaResponse | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/listMeta');
      return state.list && { ...state.list.meta as IMetaResponse };
    },

    /**
     * List Getter <br>
     * @param {IStateStrictV2<DataObjectType>} state - state from which get list
     * @returns {(IApiResponse<DataObjectType[], IMetaResponse> | undefined)} - either state.list or undefined
     */
    listResponse: (
      state: IStateStrictV2<DataObjectType>,
    ): IApiResponse<DataObjectType[], IMetaResponse> | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/listResponse');
      return state.list ? { ...state.list } : undefined;
    },

    /**
     * Single Getter <br>
     * @param {IStateStrictV2<DataObjectType>} state - state from which get single.data
     * @returns {(DataObjectType | undefined)} - either state.single.data or undefined
     */
    single: (
      state: IStateStrictV2<DataObjectType>,
    ): DataObjectType | undefined => {
      if (this.showLogging) console.log('--> STOREBASE - getters/single');
      return state.single ? { ...state.single.data } as DataObjectType : undefined;
    },

    /**
     * Edited Getter <br>
     * Retrieves the complete data object with edits.
     * This is achieved by merging the original data from `single` with any pending
     * changes stored in the `changes` state.
     * @param {IStateStrictV2<DataObjectType>} state - state from which get single.data and changes
     * @returns {DataObjectType} - state.single.data with either the pending changes or empty object
     */
    edited: (
      state: IStateStrictV2<DataObjectType>,
    ): DataObjectType => {
      if (this.showLogging) console.log('--> STOREBASE - getters/edited');
      const { single, changes } = state;
      const original = single?.data;

      const pendingChanges = changes[original?.id ?? 0] || {};

      return {
        ...original,
        ...pendingChanges,
      } as DataObjectType;
    },

    /**
     * Changes Getter <br>
     * @param {IStateStrictV2<DataObjectType>} state - state from which get data changes
     * @returns {(Record<number, Partial<DataObjectType>> | {})} - either state.changes or {}
     */
    changes: (
      state: IStateStrictV2<DataObjectType>,
    ): (Record<number, Partial<DataObjectType>> | {}) => {
      if (this.showLogging) console.log('--> STOREBASE - getters/changes');
      return state.changes;
    },

    /**
     * Getter to check if there are any changes in the store data
     * @param {IStateStrictV2<DataObjectType>} state
     * @returns {boolean}
     */
    hasChanges: (
      state: IStateStrictV2<DataObjectType>,
    ): boolean => {
      if (this.showLogging) console.log(`--> STOREBASE - getters/hasChanges - Changes: ${JSON.stringify(Object.values(state.changes))}`);
      return Object.keys(state.changes).length > 0;
    },
  };

  /**
   * Initial Mutations of the Base Store
   * @example ```
   * export default {
   *   mutations: {
   *     ...base.mutations,
   *
   *     floorplanResponse: (state: IUnitCustomState, payload: IApiResponse<ISuiteTypesFloorplans[], IMetaResponse>) => {
   *       state.floorplanResponse = payload;
   *     },
   *   }
   * }
   * ```
   */
  public mutations = {
    /**
     * Set Mutation <br>
     * Assigns Data from Payload to List & Single properties of State
     * @param {IStateStrictV2<DataObjectType>} state
     * @param {(DataObjectType | DataObjectType[] | TApiResponseWithUnion<DataObjectType>)} payload - to set
     */
    set: (
      state: IStateStrictV2<DataObjectType>,
      payload: DataObjectType | DataObjectType[] | TApiResponseWithUnion<DataObjectType>,
    ) => {
      let logOperation: string = '--> STOREBASE - mutations/set ';

      if (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) {
        if (Array.isArray((payload as any).data)) {
          state.list = payload as IApiResponse<DataObjectType[], IMetaResponse>;
          logOperation += '- list: { data, meta } ';
        } else {
          state.single = payload as IApiResponse<DataObjectType, IMetaResponse>;
          logOperation += '- single: { data, meta } ';
        }
      } else if (Array.isArray(payload as any)) {
        state.list = {
          ...state.list && state.list,
          ...{ data: payload as DataObjectType[] },
        };
        logOperation += '- list: { data } ';
      } else {
        state.single = {
          ...state.single && state.single,
          ...{ data: payload as DataObjectType },
        };
        logOperation += '- single: { data } ';
      }

      if (this.showLogging) console.log(logOperation);
    },

    /**
     * Assigns a query to either the list or single query state property based on the payload.
     * @param {IStateStrictV2<DataObjectType>} state
     * @param {object} payload - object containing the flag to determine if query it's for a list or single item and the query itself.
     * @param {boolean} payload.isListQuery - flag that is `true` if the query is for a list and `false` for a single item.
     * @param {IQueryCommandOptions} payload.query - the query command options to be stored.
     */
    setQuery: (
      state: IStateStrictV2<DataObjectType>,
      payload: {
        isListQuery: boolean;
        query: IQueryCommandOptions;
      },
    ) => {
      let logOperation: string = '--> STOREBASE - mutations/setQuery ';

      if (payload.isListQuery) {
        state.currentListQuery = payload.query;
        logOperation += '- list';
      } else {
        state.currentSingleQuery = payload.query;
        logOperation += '- single';
      }

      if (this.showLogging) console.log(logOperation);
    },

    /**
     * Updates a specific property of an item in the `changes` map for tracking local edits.
     * If the new value matches the original value in `single.data`, the change is removed,
     * effectively reverting that property's edit.
     * @param {IStateStrictV2<DataObjectType>} state
     * @param {object} payload - object with key and value to be updated
     * @param {keyof DataObjectType} payload.key - property being edited.
     * @param {DataObjectType[keyof DataObjectType]} payload.value - value for the property.
     */
    setEdited: <K extends keyof DataObjectType>(
      state: IStateStrictV2<DataObjectType>,
      payload: { key: K; value: DataObjectType[K] },
    ) => {
      const { changes, single } = state;
      const id = single?.data?.id ?? 0;
      const { key, value } = payload;

      const originalValue = single?.data?.[key];
      const isSameAsOriginal = isEqual(value, originalValue);
      const updatedChangesForId = { ...(changes[id] || {}) };

      if (isSameAsOriginal) {
        delete updatedChangesForId[key];
      } else {
        updatedChangesForId[key] = value;
      }

      if (Object.keys(updatedChangesForId).length === 0) {
        // If there are no changes for this ID, remove it from the state.changes object.
        const allChanges = { ...changes };
        delete allChanges[id];
        state.changes = allChanges;
        if (this.showLogging) {
          console.log(`--> STOREBASE - mutations/setEdited - ID: ${id}: No changes to store.`);
        }
      } else {
        // If there are changes, update the state with the new changes object.
        state.changes = {
          ...changes,
          [id]: updatedChangesForId,
        };
        if (this.showLogging) {
          console.log(`--> STOREBASE - mutations/setEdited - ID: ${id}, Key: ${String(key)}, Value: ${value}, Change stored.`, { updatedChangesForId });
        }
      }
    },

    /**
     * Resets List property of state to undefined <br>
     * @param {IStateStrictV2<DataObjectType>} state
     * @param {boolean} [includeSingle] - optional, specifies if Single property of State should be reset as well
     */
    reset: (state: IStateStrictV2<DataObjectType>, includeSingle?: boolean) => {
      let logOperation: string = '--> STOREBASE - mutations/reset - list';

      state.list = undefined;
      state.currentListQuery = {};

      if (includeSingle) {
        state.single = undefined;
        state.currentSingleQuery = {};
        logOperation += ',single';
      }

      if (this.showLogging) console.log(logOperation);
    },

    /**
     * Resets Single property of State to undefined
     * @param {IStateStrictV2<DataObjectType>} state - to reset single property
     */
    resetSingle: (state: IStateStrictV2<DataObjectType>) => {
      state.single = undefined;
      state.currentSingleQuery = {}; // Also reset the query when single is reset
      if (this.showLogging) console.log('--> STOREBASE - mutations/resetSingle - single');
    },

    /**
     * Resets the changes object in the state.
     * @param {IStateStrictV2<DataObjectType>} state
     */
    resetChanges: (state: IStateStrictV2<DataObjectType>) => {
      state.changes = {};
      if (this.showLogging) console.log('--> STOREBASE - mutations/resetChanges');
    },
  };

  /**
   * Initial Actions of the Base Store
   * @example ```
   *  export default {
   *   actions: {
   *     ...base.actions,
   *     getUnitFloorplansByUnitId: async (ctx: IAction<IStateStrictV2<ISuiteTypes>>, payload: { unitId: number }) => {
   *       ctx.commit('setFloorplanResponse', await adminRequest.get(`/property-api/units/${payload.unitId}/floorplans`));
   *     },
   *   }
   * }
   * ```
   */
  public actions = {
    /**
     * Get Action
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {IFilterPayloadV2} payload - for Data Manipulation
     * @returns {Promise<TApiResponseWithUnion<DataObjectType>>}
     */
    get: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IFilterPayloadV2<number | string>,
    ): Promise<TApiResponseWithUnion<DataObjectType>> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/get - ${(payload.id ? 'single' : 'list')}`);

      let result: TApiResponseWithUnion<DataObjectType>;
      const query: IQueryCommandOptions = payload.filters || {};

      if (payload.id && !this.resourcePath.includes(':param')) {
        result = await this.getById(payload.id, payload.filters);
      } else if (payload.id && this.resourcePath.includes(':param')) {
        result = await this.getById(payload.id, payload.filters, payload.parentId!);
      } else if (!payload.id && this.resourcePath.includes(':param')) {
        result = await this.get(payload.filters, payload.parentId!);
      } else {
        result = await this.get(payload.filters);
      }

      if (this.translationSettings && payload.additional?.includeTranslations) {
        (result as any).data = await this.getTranslations(result.data as DataObjectType | DataObjectType[]);
      }

      if (!payload.id && payload.appendToList) {
        const current: DataObjectType[] = ctx.getters.list || [];
        result = {
          data: [...current, ...(result.data as unknown as DataObjectType[])],
          meta: result.meta,
        } as IApiResponse<DataObjectType[], IMetaResponse>;
      }

      ctx.commit('set', result);
      ctx.commit('setQuery', { isListQuery: !payload.id, query });
      return result;
    },

    /**
     * Search action, which is basically a `GET` request but with `POST`, where request body is the Dynamic Router Query Command object.
     * As part of `payload`, `alias` is minimum required parameter. The advantage to using the search action is that
     * we can have a much larger set of request filters (for example, `IN` statements with a lot of values). On top of that, there are no
     * reserved characters in wildcard searches using `LIKE` operators that would normally be stripped in query strings.
     *
     * Note that the endpoint you are using this action against must be using Dynamic Router V2+ and have the `hasSearchEndpoint`
     * boolean param toggled to true in its Route Configuration.
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {ISearchCommandOptions} payload - for Data Filtering
     * @returns {Promise<IApiResponse<DataObjectType[], IMetaResponse>>}
     */
    search: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: ISearchCommandOptions,
    ): Promise<IApiResponse<DataObjectType[], IMetaResponse>> => {
      if (this.showLogging) console.log('--> STOREBASE - actions/search');
      const result = await adminRequest.post<DataObjectType[], IMetaResponse>(`${this.resourcePath}/_search`, payload);
      ctx.commit('set', result);
      ctx.commit('setQuery', { isListQuery: true, query: payload });
      return result;
    },

    /**
     * Get CSV Action <br>
     * Downloads CSV from the payload, saves either as payload.filename_report.csv OR as current_date_report.csv
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<IApiResponse<any, IMetaResponse>>}
     * @see StoreBaseImplementation.get
     */
    getCSV: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IExportFilterPayloadV2,
    ): Promise<IApiResponse<any, IMetaResponse>> => {
      const result = await this.get(payload.filters, payload.parentId, payload.fields);

      if (payload.doDownload) {
        downloadCSV(typeof result === 'string' ? result : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
      }
      return result;
    },
    /**
     * Save Action
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {IModifyPayload<number, DataObjectType>} payload - for Data Manipulation
     * @returns {Promise<void>}
     */
    save: async <TSaveIdType = number> (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IModifyPayload<TSaveIdType, DataObjectType>,
    ): Promise<void> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/save - ${(payload.id ? 'update' : 'insert')}`);

      let { resourcePath } = this;
      let { data } = payload as IGenericObject;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      if (this.translationSettings) {
        data = payload.id && data.languages
          ? await saveTranslationsInObjects<DataObjectType>(data, this.translationSettings.model)
          : this.formatNewTranslations(data);
      }

      const result = payload.id
        ? await adminRequest.put(`${resourcePath}/${payload.id!}`, subsetObject(data, ['languages']))
        : await adminRequest.post(resourcePath, subsetObject(data, ['languages']));
      const { single, list } = ctx.getters;
      const savedRecord = { ...single, ...data, ...(Array.isArray(result.data) ? result.data[0] : result.data) };

      ctx.commit('set', savedRecord);

      if (!vm()?.$rsGlobals.$isTranslateMode) {
        if (!payload.id) {
          list.unshift(savedRecord);
        } else {
          const idx = list.findIndex((v: IGenericObject) => v.id === savedRecord.id);

          if (idx !== -1) {
            list[idx] = { ...list[idx], ...savedRecord };
          }
        }

        ctx.commit('set', list);
      }
    },

    /**
     * Save Edited State Action
     * Sends the current `edited` object to the backend, optionally removing specific keys before sending,
     * and optionally refetching the single record after save.
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {Object} [payload={}] - optional, settings for the save action
     * @param {boolean} [payload.refetchSingleAfterSave=false] - optional, to re-fetch the single record after saving
     * @param {string[]} [payload.keysToRemove=[]] - optional, keys to omit from the `edited` object before sending
     * @param {Array<(edited: DataObjectType) => DataObjectType>} [payload.middleware=[]] - Array of transformation functions applied to the edited object before saving
     * @returns {Promise<void>}
     */
    saveEditedState: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: { refetchSingleAfterSave?: boolean, keysToRemove?: (keyof DataObjectType)[], middleware?: Array<(edited: DataObjectType) => DataObjectType>},
    ): Promise<void> => {
      if (this.showLogging) console.log('--> STOREBASE - actions/saveEditedState');

      const { refetchSingleAfterSave = false, keysToRemove = [], middleware = [] } = payload;
      const { commit, dispatch, getters } = ctx;
      const {
        edited, changes, hasChanges, single, list, currentSingleQuery,
      } = getters;

      if (!hasChanges) {
        console.warn('--> STOREBASE - actions/saveChanges - No changes to be saved');
        return;
      }

      const transformedEdited = middleware.reduce(
        (acc, fn) => fn(acc),
        { ...edited },
      );

      const allKeysToRemove = [...keysToRemove as string[], 'languages'];
      const data = subsetObject(transformedEdited, allKeysToRemove);
      const result = data.id
        ? await adminRequest.put(`${this.resourcePath}/${data.id}`, data)
        : await adminRequest.post(this.resourcePath, data);

      if (refetchSingleAfterSave) {
        if (Object.keys(changes).includes(String(single?.id))) {
          await dispatch('get', {
            id: data.id,
            filters: currentSingleQuery,
          });
          return;
        }
      }

      const savedRecord = { ...single, ...(Array.isArray(result.data) ? result.data[0] : result.data) };

      ctx.commit('set', savedRecord);

      if (!data.id) {
        list.unshift(savedRecord);
      } else {
        const idx = list.findIndex((v: IGenericObject) => v.id === savedRecord.id);

        if (idx !== -1) {
          list[idx] = { ...list[idx], ...savedRecord };
        }

        ctx.commit('set', list);
      }

      commit('resetChanges');
    },

    /**
     * Save Translations Action via {@link saveTranslationsInObjects}<br>
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {(IObjectWithTranslations<DataObjectType>[] | IObjectWithTranslations<DataObjectType>)} payload - for Data Manipulation
     * @returns {Promise<void>}
     */
    saveTranslations: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IObjectWithTranslations<DataObjectType>[] | IObjectWithTranslations<DataObjectType>,
    ): Promise<void> => {
      if (!this.translationSettings) {
        console.warn('No translationSettings given to store base implementation. Skipping translation saving.');
      } else {
        const result = await saveTranslationsInObjects(payload, this.translationSettings.model);
        ctx.commit('set', result);
      }
    },

    /**
     * Insert Many Action via {@link adminRequest.post} <br>
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit and get List Getter
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<DataObjectType[]>}
     */
    insertMany: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IModifyPayload<number, DataObjectType[]>,
    ): Promise<DataObjectType[]> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/insertMany - ${payload.data.length} items`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      const toInsert = (payload.data as DataObjectType[]).filter((v: any) => !v.id);
      const notInserted = (payload.data as DataObjectType[]).filter((v: any) => v.id);

      if (notInserted.length > 0) {
        /* eslint-disable-next-line */
        console.warn(`--> STOREBASE - actions/insertMany - ${notInserted.length} items with IDs. Skipping insert for these items.`);
      }

      const result: IApiResponse<DataObjectType[], null> = await adminRequest.post(
        `${resourcePath}`,
        toInsert.map((v) => subsetObject(v, ['languages'])),
      );
      const { list } = ctx.getters;

      (result.data || []).forEach((d: any, idx: number) => {
        list.unshift({
          ...toInsert[idx],
          ...d,
          ...this.translationSettings && this.formatNewTranslations(d),
        });
      });

      ctx.commit('set', list);

      return result.data!;
    },

    /**
     * Update Many Action via {@link adminRequest.put} <br>
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit and get List Getter
     * @param {IModifyPayload<number, DataObjectType[]>} payload - for Data Manipulation
     * @returns {Promise<DataObjectType[]>}
     */
    updateMany: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IModifyPayload<number, DataObjectType[]>,
    ): Promise<DataObjectType[]> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/updateMany - ${payload.data.length} items`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      let toUpdate = (payload.data as DataObjectType[]).filter((v: any) => v.id);
      const notUpdated = (payload.data as DataObjectType[]).filter((v: any) => !v.id);

      if (notUpdated.length > 0) {
        /* eslint-disable-next-line */
        console.warn(`--> STOREBASE - actions/updateMany - ${notUpdated.length} items without IDs. Skipping update for these items.`);
      }

      if (this.translationSettings && toUpdate.some((v: any) => v.languages)) {
        toUpdate = await saveTranslationsInObjects<DataObjectType>(
          toUpdate as IObjectWithTranslations<DataObjectType>[],
          this.translationSettings.model,
        ) as DataObjectType[];
      }

      const result: IApiResponse<DataObjectType[], null> = await adminRequest.put(
        `${resourcePath}`,
        toUpdate.map((v) => subsetObject(v, ['languages'])),
      );

      const { list } = ctx.getters;

      (result.data || []).forEach((d: any) => {
        const idx = list.findIndex((v: IGenericObject) => v.id === d.id);

        if (idx !== -1) {
          list[idx] = {
            ...list[idx],
            ...toUpdate.find((v: any) => v.id === d.id),
            ...d,
          };
        }
      });

      ctx.commit('set', list);

      return result.data!;
    },

    /**
     * Replaces Many to Many State Action
     * @see adminRequest.post
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit
     * @param {IRemovePayload} payload - for data manipulatons
     * @returns {Promise<void>}
     */
    replaceManyToManyState: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IModifyPayload<number, DataObjectType[]>,
    ): Promise<void> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/replaceManyToManyState`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      const result = await adminRequest.post<unknown, IJunctionPostMetaResponse<unknown[]>>(`${resourcePath}`, Array.from(payload.data, (d) => (d as IGenericObject).id));
      ctx.commit('set', result.meta!.inverseRecords);
    },

    /**
     * Remove Action <br>
     * Deletes Payload from Store using async {@link adminRequest.delete} function
     * @param {IAction<IStateStrictV2<DataObjectType>>} ctx - Action Context to Commit and get Single & List Getters
     * @param {IRemovePayload} payload - for data manipulatons
     * @returns {Promise<void>}
     */
    remove: async <TIdType extends string | number = number> (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IRemovePayload<TIdType>,
    ): Promise<void> => {
      if (this.showLogging) console.log(`--> STOREBASE - actions/remove`);

      let { resourcePath } = this;

      if (resourcePath.includes(':param')) {
        resourcePath = resourcePath.replace(':param', `${payload.parentId!}`);
      }

      await adminRequest.delete(resourcePath, [payload.id]);

      const { single, list } = ctx.getters;
      const idx = list.findIndex((v: any) => v.id === payload.id);

      if (idx !== -1) {
        list.splice(idx, 1);
        ctx.commit('set', list);
      }

      if (single?.id === payload.id) {
        ctx.commit('resetSingle');
      }
    },

    /**
     * When saving a larger number of items, use this method to split
     * out the payload into multiple request chunks to avoid 413 errors.
     * @param ctx
     * @param payload
     * @returns
     */
    bulkModify: async <IdType extends number | string>(
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: IModifyPayload<IdType, DataObjectType[]> & {
        chunkSize?: number,
        method: keyof Pick<typeof baseRequest, 'put' | 'post'>,
      },
    ): Promise<DataObjectType[]> => bulkSaveRequest({
      url: `${appConfig.adminGateway}${this.resourcePath}`,
      method: payload.method,
      payload: payload.data,
      chunkSize: payload.chunkSize,
      logChunks: this.showLogging,
    }),

    /**
     * When saving a larger number of items, use this method to split
     * out the payload into multiple request chunks to avoid errors from url being too long.
     * @param ids
     * @param chunkSize
     * @returns
     */
    bulkDelete: async (
      ctx: IAction<IStateStrictV2<DataObjectType>>,
      payload: { ids: string, chunkSize?: number },
    ): Promise<DataObjectType[]> => bulkDeleteRequest({
      url: `${appConfig.adminGateway}${this.resourcePath}`,
      ids: payload.ids,
      chunkSize: payload.chunkSize,
      logChunks: this.showLogging,
    }),
  };

  public storeModuleExport = {
    namespaced: this.namespaced,
    state: this.state,
    getters: this.getters,
    mutations: this.mutations,
    actions: this.actions,
  };

  /**
   * @param {IQueryCommandOptions} [paramFilters] - optional filters for parameters will be merged with {@link StoreBaseImplementation.defaultGetSingleFilters} into Filters Query Commands
   * @param {number} [parentId] - optional Parent ID of the object
   * @param {IBaseFieldConfig} [exportFields] - optional fields for CSV export
   * @private
   * @returns {Promise<IApiResponse>} - from {@link adminRequest.get}
   */
  private get<
    TResult extends any = any,
    TMeta extends Nullable<IMetaResponse> = IMetaResponse
  >(paramFilters?: IQueryCommandOptions, parentId?: number, exportFields?: IBaseFieldConfig[]): Promise<IApiResponse<TResult, TMeta>> {
    const filters = convertQueryCommandToQueryString(mergeQueryCommandOptionsV2([this.defaultGetListFilters, paramFilters || {}]));

    const allFilters = filters + (
      this.listViewName
        ? `${filters !== '?' ? '&' : ''}view=${this.listViewName}`
        : ''
    );

    const url = parentId
      ? `${(this.resourcePath.replace(':param', `${parentId}`))}${allFilters}`
      : `${this.resourcePath}${allFilters}`;

    return (
      exportFields
        ? adminRequest.get<TResult, TMeta>(url, '', exportFields)
        : adminRequest.get<TResult, TMeta>(url)
    );
  }

  /**
   * Gets Object from the store by ID <br>
   * Used @ {@link StoreBaseImplementation.actions.get}
   * @param {number} id - ID of the object to get
   * @param {IQueryCommandOptions} [paramFilters] - optional filters for parameters will be merged with {@link StoreBaseImplementation.defaultGetSingleFilters} into Filters Query Commands
   * @param {number} [parentId] - optional Parent ID of the object
   * @private
   * @returns {Promise<IApiResponse>} - from {@link adminRequest.get}
   */
  private getById<
    TResult extends any = any,
    TMeta extends Nullable<IMetaResponse> = IMetaResponse
  >(id: number | string, paramFilters?: IQueryCommandOptions, parentId?: number): Promise<IApiResponse<TResult, TMeta>> {
    const filters = convertQueryCommandToQueryString(mergeQueryCommandOptionsV2([this.defaultGetSingleFilters, paramFilters || {}]));

    return parentId
      ? adminRequest.get<TResult, TMeta>(`${(this.resourcePath.replace(':param', `${parentId}`))}/${id}${filters}`)
      : adminRequest.get<TResult, TMeta>(`${this.resourcePath}/${id}${filters}`);
  }
  /**
   * Formats New Translations <br> Used @ {@link StoreBaseImplementation.actions.save}, {@link StoreBaseImplementation.actions.insertMany}
   * @param {DataObjectType} data
   * @private
   * @returns {(IObjectWithTranslations<DataObjectType>) | IObjectWithTranslations<DataObjectType>[]}
   * @see IObjectWithTranslations
   */
  private formatNewTranslations(data: DataObjectType): IObjectWithTranslations<DataObjectType> | IObjectWithTranslations<DataObjectType>[] {
    const app = vm();
    return translationFormatter<DataObjectType>(
      data,
      [],
      app?.$rsGlobals.$siteSourceClientLanguages || [],
      { ...this.translationSettings!, ...{ clientId: app?.$rsGlobals.$siteSourceClientId! } },
    );
  }

  /**
   * Gets Translations if they persist @ {@link app.$rsGlobals.$siteSourceClientLanguages} <br>
   * If {@link StoreBaseImplementation.translationSettings} is undefined - the process is skipped <br>
   * Used @ {@link StoreBaseImplementation.actions.get}
   * @private
   * @returns {Promise<DataObjectType | DataObjectType[]>}
   * @param {(DataObjectType | DataObjectType[])} data
   */
  private async getTranslations(
    data: DataObjectType | DataObjectType[],
  ): Promise<DataObjectType | DataObjectType[]> {
    const app = vm();

    if (!this.translationSettings) {
      console.warn('No translationSettings given to store base implementation. Skipping translation retrieval.');
      return data;
    }

    if (app && app?.$rsGlobals.$siteSourceClientLanguages?.length > 0) {
      const translations = await getTranslationsByClientIdAndModel(
        app.$rsGlobals.$siteSourceClientId!,
        this.translationSettings.model,
      );
      return translationFormatter<DataObjectType>(
        data,
        translations,
        app?.$rsGlobals.$siteSourceClientLanguages,
        { ...this.translationSettings, ...{ clientId: app.$rsGlobals.$siteSourceClientId! } },
      ) as DataObjectType | DataObjectType[];
    }

    return data;
  }
}
